import { createBackendErrorHandler } from './errorMessages'

const globalMessages = Object.freeze({
    auth0_uid_mismatch: "Your login doesn't match this account. Please log in with the correct account.",
    authorization_header_missing: 'Please log in to continue.',
    'Authorization header is expected': 'Please log in to continue.',
    "Authorization header must be 'Bearer &lt;token&gt;'": 'Your session is invalid or has expired. Please log in again.',
    current_conn_not_set: 'Your session is invalid. Please sign in again.',
    current_full_name_not_set: 'Your session is invalid. Please sign in again.',
    current_jwt_payload_not_set: 'Your session is invalid. Please sign in again.',
    current_uid_not_set: 'Your session is invalid. Please sign in again.',
    current_user_not_set: 'Your session is invalid. Please sign in again.',
    current_username_not_set: 'Your session is invalid. Please sign in again.',
    invalid_header_format: 'Your session is invalid or has expired. Please log in again.',
    jwt_token_decode_error: 'Your session is invalid. Please log in again.',
    'JWT token has expired': 'Your session has expired. Please log in again.',
    sub_parse_error: 'The operation could not be completed. Please try again or contact support.',
    token_expired: 'Your session has expired. Please log in again.',
    'Unable to decode JWT token': 'Your session is invalid. Please log in again.',
    'Unable to match ucid to any existing user': 'Your account could not be found. Please log in again.',
    user_not_found_by_userinfo: "We couldn't find an account for your login. Please sign up or use your invitation."
})
const globalDialogs = Object.freeze({
    user_disabled: {
        dialogTitle: 'Deactivated account',
        dialogText: 'You account has been deactivated. You can request reactivation of your account at support@nuvolos.cloud.',
        dialogAction: ['logout']
    },
    instance_missing: {
        dialogTitle: 'Unavailable data',
        dialogText: 'This instance does not exist or it has been deleted.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    },
    unidentifiable_instance_request: {
        dialogTitle: 'Unavailable data',
        dialogText: 'This instance does not exist or it has been deleted.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    },
    space_missing: {
        dialogTitle: 'Unavailable data',
        dialogText: 'This space does not exist or it has been deleted.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    },
    org_missing: {
        dialogTitle: 'Unavailable data',
        dialogText: 'This organization does not exist or it has been deleted.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    },
    application_deleted: {
        dialogTitle: 'Application deleted / moved',
        dialogText: 'This application was deleted (or an older version restored), if accessing from a bookmark, please update it.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    },
    application_missing: {
        dialogTitle: 'Application not found',
        dialogText: 'This application does not seem to exist, please check the URL.',
        dialogAction: ['returnToWelcomePage'],
        primaryAction: ['returnToWelcomePage']
    }
})
const { getBackendErrorDialog: lookupGlobalDialog } = createBackendErrorHandler({ messages: globalMessages, dialogs: globalDialogs })
function getBackendErrorDialog(code) {
    if (typeof code === 'string' && /^space_\d+_in_restic(?:_staging)?$/.test(code)) {
        return {
            dialogTitle: 'Space temporarily unavailable',
            dialogText: 'This space is in backup storage and is not currently accessible. Contact your space administrator or support to restore access.',
            dialogAction: ['dismiss']
        }
    }
    return lookupGlobalDialog(code)
}

export function getGlobalBackendErrorDialog(error) {
    const code = error?.response?.data?.code
    if (code === 'user_disabled') return getBackendErrorDialog(code)
    if ((error?.response?.status === 401 || code === 'token_expired') && code !== 'vpn_credentials_missing') {
        return {
            dialogTitle: 'Your session has ended!',
            dialogText: 'Your session has expired, please reload the page.',
            dialogAction: ['reload']
        }
    }
    if (typeof code === 'string' && Object.prototype.hasOwnProperty.call(globalMessages, code)) {
        return { ...getBackendErrorDialog(code), dialogTitle: 'Account or session unavailable', dialogAction: ['reload'] }
    }
    if (['instance_missing', 'unidentifiable_instance_request', 'space_missing', 'org_missing', 'application_deleted', 'application_missing'].includes(code))
        return getBackendErrorDialog(code)
    if (typeof code !== 'string') return null
    if (/^space_\d+_in_restic(?:_staging)?$/.test(code)) return getBackendErrorDialog(code)
    if (/space_.*_migrating/.test(code)) {
        return {
            dialogTitle: 'Space is migrating',
            dialogText: 'This space is in the process of being migrated to a different region and is currently not accessible. Please check back a bit later.',
            dialogAction: ['returnToWelcomePage'],
            primaryAction: ['returnToWelcomePage']
        }
    }
    if (/space.*_in_.*/.test(code)) {
        return {
            dialogTitle: 'Space in different region',
            dialogText: 'This space is in a different region, redirecting to it in a few seconds. You might be required to sign in again.',
            dialogAction: ['dismiss']
        }
    }
    return null
}
